// @flow

import _ from 'lodash';

import { hasErrorsOfType } from '@zapier/toolbox-redux-framework';

import { CAPTCHA_REQUIRED_FIELD_ERROR } from 'app/login/constants';
import ResultTypes from 'app/login/ResultTypes';

type ResponseStub = {
  status: number,
};
type ErrorMaybeWithFields = {
  fields?: Array<{
    key: string,
    message: string,
  }>,
};
type ErrorsMaybeWithFields = Array<ErrorMaybeWithFields>;

export const toFieldErrorsObj = (errors: ErrorsMaybeWithFields) => {
  return _.chain(errors || [])
    .filter(error => !!error.fields)
    .map(error => error.fields)
    .flatten()
    .value()
    .reduce((accum, error) => {
      accum[error.key] = error.message;
      return accum;
    }, {});
};

export const loginResult = (response: ResponseStub, json: any) => {
  const { status } = response;
  if (status === 200) {
    return ResultTypes.IS_VALID_LOGIN;
  } else if (status === 401) {
    return ResultTypes.IS_BAD_CREDENTIALS;
  } else if (status === 403 && hasErrorsOfType('account_locked', json)) {
    return ResultTypes.IS_LOCKED;
  } else if (status === 403 && hasErrorsOfType('mfa_required', json)) {
    return ResultTypes.IS_2FA_REQUIRED;
  } else if (status === 403 && hasErrorsOfType('single_session', json)) {
    return ResultTypes.IS_SINGLE_SESSION;
  } else if (status === 403) {
    return ResultTypes.IS_MISSING_CSRF;
  } else if (
    status === 422 &&
    _.has(toFieldErrorsObj(json.errors), CAPTCHA_REQUIRED_FIELD_ERROR)
  ) {
    return ResultTypes.IS_CAPTCHA_REQUIRED;
  } else if (status === 429) {
    return ResultTypes.TRYING_TOO_OFTEN;
  } else {
    throw response;
  }
};

export const twoFactorResult = (response: ResponseStub) => {
  const { status } = response;
  switch (status) {
    case 200:
      return ResultTypes.IS_VALID_CODE;
    case 401:
      return ResultTypes.IS_UNAUTHENTICATED;
    case 403:
      return ResultTypes.IS_BAD_CODE;
    case 429:
      return ResultTypes.TRYING_TOO_OFTEN;
    default:
      throw response;
  }
};

export const forgotPasswordResult = (response: ResponseStub) => {
  const { status } = response;
  switch (status) {
    case 200:
      return ResultTypes.IS_SENT;
    case 401:
      return ResultTypes.IS_UNAUTHENTICATED;
    case 403:
      return ResultTypes.IS_DISABLED;
    case 404:
      return ResultTypes.NOT_FOUND;
    case 429:
      return ResultTypes.TRYING_TOO_OFTEN;
    default:
      throw response;
  }
};

export const resetPasswordResult = (response: ResponseStub) => {
  const { status } = response;
  switch (status) {
    case 200:
      return ResultTypes.IS_RESET;
    case 400:
    case 401:
    case 403:
      return ResultTypes.IS_INVALID_RESET;
    case 429:
      return ResultTypes.TRYING_TOO_OFTEN;
    default:
      throw response;
  }
};

const buildMessage = (type = '', text = '') => ({ type, text });
const buildError = _.partial(buildMessage, 'error');
const buildSuccess = _.partial(buildMessage, 'success');

const formattedMessages = {
  [ResultTypes.IS_BAD_CREDENTIALS]: () =>
    buildError('Your email or password is incorrect.'),
  [ResultTypes.IS_LOCKED]: () => buildError('Your account has been locked.'),
  [ResultTypes.IS_2FA_REQUIRED]: () =>
    buildError('Two factor authentication is required.'),
  [ResultTypes.IS_SINGLE_SESSION]: () =>
    buildError('A single session is required.'),
  [ResultTypes.IS_MISSING_CSRF]: () => buildError('Cookies must be enabled.'),
  [ResultTypes.IS_BAD_CODE]: ({ isUsingRecoveryCode }) =>
    buildError(
      isUsingRecoveryCode
        ? 'That recovery code doesn’t look right, or it has been used before.'
        : 'That code doesn’t look right.'
    ),
  [ResultTypes.IS_SENT]: () => buildSuccess('Sent!'),
  [ResultTypes.IS_DISABLED]: () =>
    buildError('Account is disabled, contact support.'),
  [ResultTypes.TRYING_TOO_OFTEN]: () =>
    buildError('You’re trying too frequently.'),
  [ResultTypes.NOT_FOUND]: () => buildError('No users exist with that email.'),
  [ResultTypes.IS_RESET]: () => buildSuccess('Reset successfully.'),
  [ResultTypes.IS_INVALID_RESET]: () => buildError('Reset was invalid.'),
  [ResultTypes.IS_UNAUTHENTICATED]: () => buildError('Not authenticated.'),
  default: ({ hasSessionTimedOut, hasGoogleOneTapError }) => {
    if (hasSessionTimedOut) {
      return buildError('Your session timed out. Please log in again!');
    }

    if (hasGoogleOneTapError) {
      return buildError('Oops, looks like we had an error signing you in!');
    }

    return buildMessage();
  },
};

type Options = {
  hasSessionTimedOut?: boolean,
  hasGoogleOneTapError?: boolean,
};
export const formattedMessageForResult = (
  result: string,
  options?: Options
) => {
  const formatter = formattedMessages[result] || formattedMessages.default;
  return formatter(options || {});
};

export const parseUsernameAndCode = (hash: ?string): [string, string] => {
  const match = (hash || '').match(/^#?(.*);(.*)/);
  const [username = '', code = ''] = match ? match.slice(1) : [];
  return [username, code];
};
